import { useMemo, useCallback, useRef, useEffect, useState, type JSX } from "react";
import type { MazeGridProps, CellType, Position, Collectible } from "./types";
import { MazeCell } from "./MazeCell";

/**
 * MazeGrid Component
 *
 * The main container for the programming maze challenge. Creates a
 * configurable grid with obstacles, collectibles, start/goal positions,
 * and renders children (MazeRunner) positioned absolutely within.
 *
 * Features warm copper/orange tech-toy aesthetic matching Pixel's theme.
 *
 * @example
 * <MazeGrid
 *   size={[5, 5]}
 *   start={[0, 0]}
 *   end={[4, 4]}
 *   obstacles={[[1, 1], [2, 2]]}
 *   collectibles={[{ position: [2, 0], type: 'star', collected: false }]}
 * >
 *   <MazeRunner position={[0, 0]} direction="right" cellSize={80} gridGap={8} />
 * </MazeGrid>
 */
export function MazeGrid({
  size,
  start,
  end,
  obstacles = [],
  collectibles = [],
  isExecuting = false,
  discipline = "programming",
  children,
}: MazeGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(80);
  const gridGap = 8;

  const [rows, cols] = size;

  // Calculate cell size based on container width
  useEffect(() => {
    const updateCellSize = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        // Account for padding (32px each side = 64px total) and gaps
        const availableWidth = containerWidth - 64;
        const totalGaps = (cols - 1) * gridGap;
        const calculatedCellSize = Math.floor((availableWidth - totalGaps) / cols);
        // Clamp between 48px (min for touch) and 96px (max for aesthetics)
        setCellSize(Math.min(96, Math.max(48, calculatedCellSize)));
      }
    };

    updateCellSize();
    window.addEventListener("resize", updateCellSize);
    return () => window.removeEventListener("resize", updateCellSize);
  }, [cols]);

  // Helper to check if position matches
  const positionEquals = useCallback((a: Position, b: Position) => {
    return a[0] === b[0] && a[1] === b[1];
  }, []);

  // Get cell type for a given position
  const getCellType = useCallback(
    (row: number, col: number): CellType => {
      const pos: Position = [row, col];

      if (positionEquals(pos, start)) return "start";
      if (positionEquals(pos, end)) return "goal";
      if (obstacles.some((obs) => positionEquals(obs, pos))) return "obstacle";

      const collectible = collectibles.find((c) => positionEquals(c.position, pos));
      if (collectible && !collectible.collected) return "collectible";

      return "empty";
    },
    [start, end, obstacles, collectibles, positionEquals]
  );

  // Get collectible at position (if any)
  const getCollectible = useCallback(
    (row: number, col: number): Collectible | undefined => {
      const pos: Position = [row, col];
      return collectibles.find((c) => positionEquals(c.position, pos));
    },
    [collectibles, positionEquals]
  );

  // Generate grid cells
  const gridCells = useMemo(() => {
    const cells: JSX.Element[] = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const cellType = getCellType(row, col);
        const collectible = getCollectible(row, col);

        cells.push(
          <MazeCell
            key={`${row}-${col}`}
            type={cellType}
            position={[row, col]}
            collectible={collectible}
            disabled={isExecuting}
          />
        );
      }
    }

    return cells;
  }, [rows, cols, getCellType, getCollectible, isExecuting]);

  // Calculate grid dimensions for the runner positioning
  const gridWidth = cols * cellSize + (cols - 1) * gridGap;
  const gridHeight = rows * cellSize + (rows - 1) * gridGap;

  return (
    <div
      ref={gridRef}
      className="
        relative w-full max-w-[600px] mx-auto
        bg-gradient-to-br from-pixel-light/20 via-white to-pixel-light/10
        rounded-3xl p-6 md:p-8
        border-4 border-pixel/30
        shadow-[0_8px_32px_rgba(255,140,66,0.15),inset_0_2px_0_rgba(255,255,255,0.8)]
      "
    >
      {/* Decorative corner gears */}
      <div className="absolute top-3 left-3 text-2xl opacity-40 animate-spin-slow">
        ⚙️
      </div>
      <div
        className="absolute top-4 right-4 text-xl opacity-30 animate-spin-slow-reverse"
        style={{ animationDelay: "0.5s" }}
      >
        ⚙️
      </div>
      <div
        className="absolute bottom-4 left-4 text-xl opacity-30 animate-spin-slow"
        style={{ animationDelay: "1s" }}
      >
        ⚙️
      </div>
      <div
        className="absolute bottom-3 right-3 text-2xl opacity-40 animate-spin-slow-reverse"
        style={{ animationDelay: "1.5s" }}
      >
        ⚙️
      </div>

      {/* Circuit line decorations */}
      <div className="absolute top-0 left-1/4 w-px h-6 bg-gradient-to-b from-transparent via-pixel-accent/30 to-transparent" />
      <div className="absolute top-0 right-1/3 w-px h-8 bg-gradient-to-b from-transparent via-pixel-accent/20 to-transparent" />
      <div className="absolute bottom-0 left-1/3 w-px h-6 bg-gradient-to-t from-transparent via-pixel/30 to-transparent" />
      <div className="absolute bottom-0 right-1/4 w-px h-8 bg-gradient-to-t from-transparent via-pixel/20 to-transparent" />

      {/* Grid container */}
      <div
        className="
          relative
          mx-auto
          bg-gradient-to-b from-neutral-100/80 to-neutral-200/60
          rounded-2xl p-4
          border-2 border-neutral-300/60
          shadow-[inset_0_2px_8px_rgba(0,0,0,0.05)]
        "
        style={{ width: "fit-content" }}
      >
        {/* The actual grid */}
        <div
          className="grid relative"
          style={{
            gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
            gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
            gap: `${gridGap}px`,
            width: gridWidth,
            height: gridHeight,
          }}
          role="grid"
          aria-label={`${rows} by ${cols} maze grid`}
        >
          {gridCells}

          {/* Children container (MazeRunner) - positioned relative to grid */}
          <div
            className="absolute top-0 left-0 pointer-events-none"
            style={{ width: gridWidth, height: gridHeight }}
          >
            {children}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 rounded-full border border-neutral-200">
          <span className="text-base">🚀</span>
          <span className="font-body text-neutral-600">Start</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 rounded-full border border-neutral-200">
          <span className="text-base">🏁</span>
          <span className="font-body text-neutral-600">Goal</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 rounded-full border border-neutral-200">
          <span className="text-base">⚙️</span>
          <span className="font-body text-neutral-600">Blocked</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 rounded-full border border-neutral-200">
          <span className="text-base">⭐</span>
          <span className="font-body text-neutral-600">Collect</span>
        </div>
      </div>
    </div>
  );
}

export default MazeGrid;
